import {
  useState,
  useEffect,
  forwardRef,
  useMemo,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Loader from "react-spinner-loader";
// Import services and utilities
import {
  getData,
  postDataWithBody,
  putDataWithBody,
} from "../../../../../Services/Services";
import {
  deleteResourcebyId,
  getbarches,
  Allassetcategory,
  rolebaseuser,
  getThemeURL,
  getassetsubcategory,
  stafftype,
  getallBanksByRole,
  getallresourcesByRole
} from "../../../../../Services/EndPoints";
// Import icons
import {
  EditConvgIcon,
  DeleteConvgIcon,
  Viewicon,
} from "../../../../../Components/IconComponents";
import Confirmpopup from "../../../Common/ConfirmPopup/Confirmpopup";
import ExpandaleResource from "../../../../../Components/UiComponents/Table/ExpandableResource";
import { format, parseISO } from "date-fns";
import { setTableData } from "../../../../../CentralStore/ExportLMSTable/dataSlice";
import { Resourcedownload } from "../../../../../JsonData/Resourcedownload";

// Main component for displaying and managing Resource data in a table format
const ResourceTable = forwardRef((props, ref) => {
  // Core data state
  const [loader, setLoader] = useState(false);
  const [resourceData, setResourceData] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [rowCount, setRowCount] = useState(0);

  // User and role management state
  const [assignedUsers, setAssignedUsers] = useState({});
  const [roleUsersFetching, setRoleUsersFetching] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userProfileLoaded, setUserProfileLoaded] = useState(false);

  // Dropdown data state
  const [banksList, setBanksList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staffTypes, setStaffTypes] = useState([]);
  const [assetcategory1, setassetcategory] = useState([]);
  const [userPermissions, setUserPermissions] = useState({
    canView: false,
    canEdit: false,
    canDelete: false,
  });
  // Search and export state
  const [searchData, setSearchData] = useState([]);

  // Hooks initialization
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Effect to send data to central store when search data changes
  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);

  // Initial data fetching on component mount
  useEffect(() => {
    fetchUserProfile();
    fetchcategory();
    fetchstaffdetails();
  }, []);
  const parseUserPermissions = (webPermissions) => {
    if (!webPermissions) return { canView: false, canEdit: false, canDelete: false };
    const perms = webPermissions.split(",").map(p => p.trim().toUpperCase());
    return {
      canView: perms.includes("V"),
      canEdit: perms.includes("E"),
      canDelete: perms.includes("D"),
    };
  };
  /* Fetch staff types from API */
  const fetchstaffdetails = async () => {
    try {
      let request = ["STAFF_CAT"];
      const response = await postDataWithBody(stafftype, request, {});
      if (Array.isArray(response)) {
        setStaffTypes(response);
      }
    } catch (error) {
      console.error("Error fetching staff details:", error);
    }
  };

  /* Fetch asset categories from API */
  const fetchcategory = async () => {
    try {
      let request = ["ASSET_CAT"];
      const response = await postDataWithBody(Allassetcategory, request, {});
      if (response && Array.isArray(response)) {
        setassetcategory(response);
      }
    } catch (error) {
      console.error("Error fetching asset categories:", error);
    }
  };

  /* Create payload for bank list API based on user profile */
  const createBankListPayload = (userProfile) => {
    if (!userProfile) return null;

    const { role_name, bank_id, branch_id } = userProfile;

    // Determine admin_role based on role_name
    let admin_role = "U"; // default for other roles
    if (role_name === "HO") {
      admin_role = "HO";
    } else if (role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: bank_id || null,
      branch_id: branch_id || null
    };
  };

  // Fetch banks and branches after user profile is loaded
  useEffect(() => {
    const fetchBanksAndBranches = async () => {
      if (!userProfile) return; // Wait for user profile to be loaded

      try {
        // Create payload for banks API
        const bankPayload = createBankListPayload(userProfile);

        if (bankPayload) {
          // Fetch banks with payload
          const banksResponse = await postDataWithBody(getallBanksByRole, bankPayload, {});
          if (banksResponse && Array.isArray(banksResponse)) {
            setBanksList(banksResponse);

            // Fetch branches for each bank
            const branchPromises = banksResponse.map((bank) =>
              getData(getbarches.replace("ID", bank.id))
            );

            const branchResponses = await Promise.all(branchPromises);
            const allBranches = branchResponses.flat().filter((branch) => branch);

            setBranches(allBranches);
          }
        }
      } catch (error) {
        console.error("Error fetching banks and branches:", error);
        toast.error("Failed to load banks and branches");
      }
    };

    fetchBanksAndBranches();
  }, [userProfile]);

  // Fetch resource list after user profile and userId are available
  useEffect(() => {
    if (userId && userProfileLoaded) {
      getUserProfile();

      // Show success message if passed from location
      if (props.locationInstance && props.locationInstance.msg) {
        toast.success(props.locationInstance.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    }
  }, [userId, userProfileLoaded]);

  // Fetch users for each assigned team after resources are loaded
  useEffect(() => {
    const fetchAllUsersByRoles = async () => {
      if (!resourceData.length || roleUsersFetching) return;

      try {
        setRoleUsersFetching(true);

        // Get unique assigned team IDs
        const assignedTeamIds = [
          ...new Set(
            resourceData
              .filter((resource) => resource.assigned_team)
              .map((resource) => resource.assigned_team)
          ),
        ];

        // Fetch users for each team
        const userPromises = assignedTeamIds.map(async (roleId) => {
          try {
            const response = await getData(rolebaseuser.replace("ID", roleId));
            return { roleId, users: Array.isArray(response) ? response : [] };
          } catch (error) {
            console.error(`Error fetching users for role ${roleId}:`, error);
            return { roleId, users: [] };
          }
        });

        const results = await Promise.all(userPromises);

        // Create a map of role IDs to users
        const usersMap = results.reduce((acc, { roleId, users }) => {
          acc[roleId] = users;
          return acc;
        }, {});

        setAssignedUsers(usersMap);
      } catch (error) {
        console.error("Error fetching users for roles:", error);
      } finally {
        setRoleUsersFetching(false);
      }
    };

    fetchAllUsersByRoles();
  }, [resourceData]);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    getUserProfile,
    refreshData: getUserProfile, // Alternative name for clarity
  }));

  /* Get category name by ID */
  const getCategoryName = (id) => {
    const category = assetcategory1.find((cat) => cat.id === id);
    return category ? category.value : "NA";
  };

  /* Fetch user profile and set userId */
  const fetchUserProfile = async () => {
    try {
      setLoader(true);
      const response = await getData(getThemeURL);
      if (response && Array.isArray(response) && response.length > 0) {
        setUserProfile(response[0]); // Store the complete user profile
        setUserId(response[0].id); // Get `id` from the first user
        const permissions = parseUserPermissions(response[0].web_permissions);
      setUserPermissions(permissions);
      } else {
        // Fallback to localStorage or other auth mechanism
        const fallbackUserId =
          localStorage.getItem("userId") || "default-user-id";
        setUserId(fallbackUserId);
        console.warn("Using fallback user ID:", fallbackUserId);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      // Set a default user ID if API fails
      const fallbackUserId =
        localStorage.getItem("userId") || "default-user-id";
      setUserId(fallbackUserId);
    } finally {
      setUserProfileLoaded(true);
      setLoader(false);
    }
  };

  /* Sort resources by request date (newest first)*/
  const sortResourcesById = (resources) => {
    return [...resources].sort((a, b) => {
      const dateA = new Date(a.requestDateAndTime);
      const dateB = new Date(b.requestDateAndTime);
      return dateB - dateA; // Newest first
    });
  };

  /* Get user profile and trigger resource list fetch */
  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        getResourceList(userProfile[0]);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  /* Create payload for resource list API */
  const createPayload = (profile) => {
    let admin_role = "U"; // default value

    if (profile.role_name === "HO") {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null
    };
  };

  /* Fetch resource list from API */
  const getResourceList = async (profile) => {
    try {
      setLoader(true);
      if (!userId) {
        console.error("Cannot fetch resources: userId is null");
        return;
      }

      const payload = createPayload(profile);
      const resourceList = await postDataWithBody(getallresourcesByRole, payload, {});
      const sortedResources = sortResourcesById(resourceList);
      setResourceData(sortedResources);
      setRowCount(sortedResources.length);
      sendDataToCentralStore(resourceList);
    } catch (error) {
      toast.error("Failed to fetch resources", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setLoader(false);
    }
  };

  /* Check if current user is assigned to a specific resource */
  const isCurrentUserAssigned = (row) => {
    const { requestType, staff_resource, asset_resource } = row.original;

    let assignedToId = null;
    if (requestType === "ASSET" && Array.isArray(asset_resource) && asset_resource[0]) {
      assignedToId = asset_resource[0].assignedTo;
    } else if (requestType === "STAFF" && Array.isArray(staff_resource) && staff_resource[0]) {
      assignedToId = staff_resource[0].assignedTo;
    }

    return assignedToId && userId && assignedToId.toString() === userId.toString();
  };

  // Check if any resource is assigned to current user (for request resource button)
  const isAnyResourceAssignedToCurrentUser = useMemo(() => {
    return resourceData.some(resource => {
      const { requestType, staff_resource, asset_resource } = resource;

      let assignedToId = null;
      if (requestType === "ASSET" && Array.isArray(asset_resource) && asset_resource[0]) {
        assignedToId = asset_resource[0].assignedTo;
      } else if (requestType === "STAFF" && Array.isArray(staff_resource) && staff_resource[0]) {
        assignedToId = staff_resource[0].assignedTo;
      }

      return assignedToId && userId && assignedToId.toString() === userId.toString();
    });
  }, [resourceData, userId]);

  /* Handle adding new resource - check if user can create new resource */
  const addNewHandler = () => {
    // Don't allow if current user is assigned to any resource
    if (isAnyResourceAssignedToCurrentUser) {
      toast.warning("You cannot request a new resource as you are already assigned to an existing resource.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }
    navigate("/configurations/resource-list/create-resource");
  };

  /* Get user name by user ID and team ID */
  const getUserNameById = (userId, teamId) => {
    if (!userId || !teamId) return "NA";

    const teamUsers = assignedUsers[teamId] || [];
    const user = teamUsers.find((user) => user.id === userId);

    return user?.user_name || "NA";
  };

  /* Render expanded row details for table */
  const renderExpandedRow = (row) => {
    const { requestType, staff_resource, asset_resource, assigned_team } =
      row.original;

    // Find bank and branch details
    const bank = banksList.find((b) => b.id === row.original.bankId);
    const branch = branches.find((b) => b.id === row.original.branchId);

    // Get assigned user data
    let assignedToId = null;
    if (
      requestType === "ASSET" &&
      Array.isArray(asset_resource) &&
      asset_resource[0]
    ) {
      assignedToId = asset_resource[0].assignedTo;
    } else if (
      requestType === "STAFF" &&
      Array.isArray(staff_resource) &&
      staff_resource[0]
    ) {
      assignedToId = staff_resource[0].assignedTo;
    }

    const assignedUserName = getUserNameById(assignedToId, assigned_team);

    return (
      <div className="expanded-row-details p-3">
        <div className="row">
          <div className="col-md-6">
            <h4>Resource Details</h4>
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <th>Bank Name</th>
                  <td>{bank ? bank.bankName : "N/A"}</td>
                </tr>
                <tr>
                  <th>Branch Name</th>
                  <td>{branch ? branch.branchName : "N/A"}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{row.original.email || "N/A"}</td>
                </tr>
                <tr>
                  <th>Phone Number</th>
                  <td>{row.original.phoneNumber || "N/A"}</td>
                </tr>
                <tr>
                  <th>Description</th>
                  <td>{row.original.description || "N/A"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-md-6">
            {/* Staff Resource Details */}
            {requestType === "STAFF" &&
              staff_resource &&
              staff_resource.length > 0 && (
                <div style={{display:'flex'}}>
                  <h4>Staff Resource Details</h4>
                  <table className="table table-bordered" >
                    <tbody>
                      <tr>
                        <th>Skills</th>
                        <td>{staff_resource[0].skills || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Employment Type</th>
                        <td>{staff_resource[0].employmentType || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Experience Level</th>
                        <td>{staff_resource[0].expLevel || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Assigned To</th>
                        <td>{assignedUserName}</td>
                      </tr>
                      <tr>
                        <th>Remarks</th>
                        <td>{staff_resource[0].remarks || "N/A"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

            {/* Asset Resource Details */}
            {requestType === "ASSET" &&
              asset_resource &&
              asset_resource.length > 0 && (
                <div>
                  <h4>Asset Resource Details</h4>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>Asset Name</th>
                        <td>{asset_resource[0].assetName || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Asset Type ID</th>
                        <td>{asset_resource[0].assetTypeId || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Asset Category ID</th>
                        <td>{asset_resource[0].assetCategoryId || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Quantity</th>
                        <td>{asset_resource[0].quantity || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Non-Functional Units</th>
                        <td>{asset_resource[0].nonFunctionalUnits || "N/A"}</td>
                      </tr>
                      <tr>
                        <th>Assigned To</th>
                        <td>{assignedUserName}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>
    );
  };

  /* Handle view resource details navigation */
  const handleViewATM = (data) => {
    // Find the associated bank and branch
    const bank = banksList.find((bank) => bank.id === data.bankId);
    const branch = branches.find((branch) => branch.id === data.branchId);

    // Navigate to ATM details view with data
    navigate("/configurations/resource-list/res-view", {
      state: {
        data,
        bank,
        branch,
      },
    });
  };

  /* Get resource status from staff or asset resource */
  const getResourceStatus = (row) => {
    const { requestType, staff_resource, asset_resource } = row.original;

    if (
      requestType === "STAFF" &&
      staff_resource &&
      staff_resource.length > 0
    ) {
      return staff_resource[0].status;
    }
    if (
      requestType === "ASSET" &&
      asset_resource &&
      asset_resource.length > 0
    ) {
      return asset_resource[0].status;
    }
    return null;
  };

  /* Get staff name by ID */
  const getStaffNameById = (id) => {
    const staff = staffTypes.find((item) => item.id === id);
    return staff?.value || "NA";
  };

  /* Send processed data to central Redux store for export functionality */
  const sendDataToCentralStore = async (data) => {
    if (data.length > 0) {
      const columns = Resourcedownload;

      // Get unique asset category IDs for subcategory fetching
      const assetTypeIds = [...new Set(data.map(item => item.asset_resource?.[0]?.assetCategoryId).filter(Boolean))];

      // Fetch all subcategories for those IDs
      const subCategoryResponses = await Promise.all(
        assetTypeIds.map(id =>
          getData(getassetsubcategory.replace("ID", id)).then(res => ({ id, res }))
        )
      );

      // Build subcategory map for lookup
      const subCategoryMap = {};
      subCategoryResponses.forEach(({ id, res }) => {
        const subList = Array.isArray(res) ? res : res?.data || [];
        subList.forEach((sub) => {
          subCategoryMap[sub.id] = sub.value;
        });
      });

      // Transform data for export
      const exportData = data.map((item) => {
        const bank = banksList.find((b) => b.id === item.bankId);
        const branch = branches.find((b) => b.id === item.branchId);
        const staff = item.staff_resource && item.staff_resource.length > 0 ? item.staff_resource[0] : {};
        const asset = item.asset_resource && item.asset_resource.length > 0 ? item.asset_resource[0] : {};

        // Get assigned user ID
        let assignedToId = null;
        if (item.requestType === "ASSET" && item.asset_resource?.[0]) {
          assignedToId = item.asset_resource[0].assignedTo;
        } else if (item.requestType === "STAFF" && item.staff_resource?.[0]) {
          assignedToId = item.staff_resource[0].assignedTo;
        }

        const assignedUser = assignedUsers[item.assigned_team]?.find(
          (user) => user.id === assignedToId
        )?.user_name || "NA";

        return {
          "Request ID": item.resourceId || "NA",
          "Resource Title": item.jobTitle || "NA",
          "Bank Name": bank?.bankName || "NA",
          "Branch Name": branch?.branchName || "NA",
          "Request Type": item.requestType || "NA",
          "Requested Date & Time": item.requestDateAndTime
            ? format(parseISO(item.requestDateAndTime), "dd-MM-yyyy hh:mm a")
            : "NA",
          "Assigned User": assignedUser,
          "Email": item.email,
          "Phone Number": item.phoneNumber,
          "Department": item.reqByDepartment,
          "Resource Description": item.description,
          // Staff-specific fields
          "Staff Type": getStaffNameById(staff.staffId) || "NA",
          "Required Skill Set": staff.skills || "NA",
          "Employment Type": staff.employmentType || "NA",
          "Experience Level": staff.expLevel || "NA",
          // Asset-specific fields
          "Asset Name": asset.assetName || "NA",
          "Asset Type": subCategoryMap[asset.assetTypeId] || "NA",
          "Asset Category": getCategoryName(asset.assetCategoryId) || "NA",
          "Quantity Required": asset.quantity || "NA",
          "Non-Functional Units": asset.nonFunctionalUnits || "NA",
        }
      });

      // Dispatch to Redux store
      dispatch(
        setTableData({
          columns,
          tableData: exportData,
        })
      );
    }
  };

  // Define table columns with memoization for performance
  const columns = useMemo(
    () => [
      {
        Header: t("Request ID"),
        accessor: "resourceId",
        Cell: ({ value }) => value || "NA",
      },
      {
        Header: t(" Resource Title"),
        accessor: "jobTitle",
        disableSortBy: true,
        Cell: ({ value }) => value || "NA",
      },
      {
        Header: t("Bank Name"),
        accessor: "bankId",
        Cell: ({ row }) => {
          const bank = banksList?.find((b) => b.id === row.original.bankId);
          return bank ? bank.bankName : "NA";
        },
      },
      {
        Header: t("Branch Name"),
        accessor: "branchId",
        disableSortBy: true,
        Cell: ({ row }) => {
          const branch = branches?.find((b) => b.id === row.original.branchId);
          return branch ? branch.branchName : "NA";
        },
      },
      {
        Header: t("Request Type"),
        accessor: "requestType",
        Cell: ({ value }) => value || "N/A",
      },
      {
        Header: t("Requested Date & Time"),
        accessor: "requestDateAndTime",
        disableSortBy: true,
        Cell: ({ value }) =>
          value ? format(parseISO(value), "dd-MM-yyyy hh:mm a") : "NA",
      },
      {
        Header: t("Assigned User"),
        accessor: "assigned_user",
        Cell: ({ row }) => {
          const { requestType, staff_resource, asset_resource, assigned_team } =
            row.original;

          // Get the user ID from the appropriate resource type
          let userId = null;
          if (
            requestType === "ASSET" &&
            Array.isArray(asset_resource) &&
            asset_resource[0]
          ) {
            userId = asset_resource[0].assignedTo;
          } else if (
            requestType === "STAFF" &&
            Array.isArray(staff_resource) &&
            staff_resource[0]
          ) {
            userId = staff_resource[0].assignedTo;
          }

          return getUserNameById(userId, assigned_team);
        },
      },
      ...(userPermissions.canView || userPermissions.canEdit || userPermissions.canDelete
      ? [{
        Header: t("Actions"),
        disableSortBy: true,
        width: 54,
        Cell: ({ row }) => {
          const [isdelete, setIsdelete] = useState(false);
          const status = getResourceStatus(row);
          const isEditable = status === 768;
          const isUserAssigned = isCurrentUserAssigned(row);

          const deletePopUp = () => setIsdelete(!isdelete);

          const deleteTableDataById = async (id) => {
            try {
              const data = { id: id };
              const res = await putDataWithBody(deleteResourcebyId, data, {});
              if (res.status === "success") {
                toast.success(res.msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
              } else {
                toast.error("Delete failed", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
              }
            } catch {
              toast.error("Error deleting resource", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            }
            setIsdelete(false);
          };

          return (
            <div className="resource__actions" style={{ width: "54px" }}>
              {/* View Action */}
              {userPermissions.canView && (
                <span onClick={() => handleViewATM(row.original)}>
                  <Viewicon className="cursor-pointer me-2" />
                </span>
              )}

              {/* Edit Action */}
              {userPermissions.canEdit && (
                <span
                  className="resource__edit-icon"
                  onClick={() => {
                    if (isEditable && !isUserAssigned) {
                      navigate("/configurations/resource-list/create-resource", {
                        state: { data: row.original, edit: true },
                      });
                    } else if (isUserAssigned) {
                      toast.warning("You cannot edit a resource assigned to you.", {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                      });
                    }
                  }}
                  style={{
                    cursor: isEditable && !isUserAssigned ? "pointer" : " not-allowed",
                    opacity: isEditable && !isUserAssigned ? 1 : 0.5,
                  }}
                >
                  <EditConvgIcon />
                </span>
              )}

              {/* Delete Action */}
              {userPermissions.canDelete && (
                <span
                  onClick={deletePopUp}
                  style={{
                    cursor: "pointer",
                    opacity: 1,
                    pointerEvents: "auto",
                  }}
                >
                  <DeleteConvgIcon className="cursor-pointer" />
                </span>
              )}

              {/* Delete Confirmation Modal */}
              {isdelete && (
                <Confirmpopup
                  show={isdelete}
                  title={t("Confirm Delete")}
                  message={t("Are you sure you want to delete")}
                  handleToggle={deletePopUp}
                  row={row}
                  deleteRow={() => deleteTableDataById(row.original.resourceId)}
                  cancel={t("Cancel")}
                  action={t("Delete")}
                />
              )}
            </div>
          );
        },
      }]
      : []),
    ],
    [t, navigate, banksList, branches, assignedUsers, userId, userPermissions]
  );

  return (
    <div className="resource-table-container">
      {/* Loading indicator */}
      <Loader
        show={loader || roleUsersFetching}
        type="body"
        stack="vertical"
        message="Loading Data"
      />

      {/* Main expandable table component */}
      <ExpandaleResource
        data={resourceData}
        columns={columns}
        addButtonLabel={t("Request Resource")}
        addNewHandler={addNewHandler}
        numberOfRows={10}
        rowCount={rowCount}
        showExport={false}
        setSearchData={setSearchData}
        showFilter={false}
        showReset={false}
        onExpand={false}
        renderExpandedRow={renderExpandedRow}
        resourceData={resourceData}
        currentUserId={userId}
        getResourceList={getUserProfile}
        headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF", position: 'sticky', // Ensure sticky positioning
            top: 0,
            zIndex: 1000, }}
        tableStyle={{
          border: "1px solid #f0f0f0",
          borderRadius: "4px",
          padding:'0px'
        }}
        resourceButton={true}
        // Disable the request resource button if user is assigned to any resource
        disableAddButton={isAnyResourceAssignedToCurrentUser}
      />
    </div>
  );
});

export default ResourceTable;